import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { AvatarTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-avatar-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './avatar-wrapper.component.html',
  styleUrl: './avatar-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxAvatarWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<AvatarTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  initials: Signal<string>

  readonly imagePath = computed(() => {
    const image = this.flowxProps()?.image
    if (!image?.value) {
      return null
    }
    return getContentIcon(image)
  })

  readonly shapeClass = computed(() => `avatar--${this.flowxProps()?.shape || 'circle'}`)
  readonly sizeClass = computed(() => `avatar--${this.flowxProps()?.size || 'md'}`)
  readonly statusColorClass = computed(
    () => `avatar__status--${this.flowxProps()?.statusColor || 'offline'}`
  )
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.shapeClass(), this.sizeClass()].filter(Boolean).join(' ')
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(props.hide, this.templateConfig())
      this.initials = getDynamicPropSignal(props.initials || '', this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
